import {Component, h, Host, Prop, State} from "@stencil/core";
import {fileNameFromHeader, saveAs} from "../../common/filesaver";
import {FeedbackParams} from "../lb-antrag";
import {_debug} from "../../common/debugger/debugger";

export interface SuccessMessage {
    formId: string
    formTitle: string
    vorgangsnummer: string
    timestamp: string
    actions: []
    traceId?: string
}

@Component({
    tag: "lb-form-success",
    styleUrl: "success.scss",
    shadow: true,
})
export class Success {

    @Prop() successMessage:SuccessMessage;
    @Prop() downloadUrl:string; // url
    @Prop() generatePdfEndpoint:string; // url
    @Prop() offline:boolean;
    @Prop() data:string; // JSON string
    @Prop() feedbackParams:FeedbackParams;

    /**
     * i18n - "meta" texts in selected language
     */
    @Prop() i18n;
    @Prop({ reflect: true }) language: string = "de";
    @Prop() appConfig;

    @State() showDialog = true;
    @State() error;

    constructor() {
        _debug("lb-form-success.constructor()");
    }
    disconnectedCallback() {
        _debug("lb-form-success.disconnectedCallback()");
    }
    connectedCallback() {
        _debug("lb-form-success.connectedCallback()");
    }
    componentWillRender() {
        _debug("lb-form-success.componentWillRender()");
    }
    componentDidRender() {
        _debug("lb-form-success.componentDidRender()");
    }

    download() {

        fetch(this.generatePdfEndpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-TOKEN": "true",
                Prefer: "example=bundesadler", // antrag-erstellt, leistung-offline, retry
            },
            body: this.data, // body data type must match "Content-Type" header
        }).then((resp) => {
            if (resp.ok) {
                resp.blob().then((blob) => {
                    saveAs(blob, fileNameFromHeader(resp.headers.get("content-disposition"), "Antrag.pdf"));
                });               
            } else {
               if (resp.status === 408 || resp.status === 504) {
                   this.error = ({
                       error: "Timeout",
                       timestamp: new Date().toISOString(),
                       traceId: "n/a",
                   });
               } else {
                   resp["json"]().then((result) => {
                       this.error = result;
                   });
               }
            }
        });
    }

    render() {
        return (
            <Host>
                { !this.offline && this.successMessage && this.downloadUrl && (
                  <div class="success">
                    {window.scroll(0, 0)}
                    <h4>{this.i18n?.beantragung?.formSubmitted}</h4>
                    <p class="hint">
                      {this.i18n?.beantragung?.applicationDownloadHint}
                    </p>

                    <c-button primary large href={this.downloadUrl}>
                      <muse-icon name="file-download"></muse-icon>
                      <span>{this.i18n?.beantragung?.applicationDownloadBtnText}</span>
                    </c-button>

                    <c-markdown-renderer>
                    <div slot="source">
                        {this.i18n?.beantragung?.saveVorgangsnummerHint}
                      </div>
                    </c-markdown-renderer>

                    <c-format-string slot="value" padding="4">
                      {this.successMessage.vorgangsnummer}
                    </c-format-string>

                    {this.feedbackParams && (
                      <f-feedback source={window.location.pathname}
                                  issue={this.feedbackParams.issue}
                                  region={this.feedbackParams.region}
                                  issue-type="procedure"
                                  instrument-id="online-proc">
                      </f-feedback>
                    )}

                    <c-modal
                      visible={this.showDialog}
                      onClose={() => (this.showDialog = false)}
                      backdrop
                    >
                      <c-container slot="content" size="sm">
                        <c-header type="h2" role="heading">
                          {this.i18n?.beantragung?.applicationSubmitted}
                        </c-header>
                        <c-markdown-renderer>
                          <div slot="source">
                            {this.i18n?.beantragung?.applicationDownloadText}
                          </div>
                        </c-markdown-renderer>

                        <div class="confirm-buttons">
                          <c-button
                            primary
                            centered-content
                            block
                            large
                            href={this.downloadUrl}
                            onClick={() => this.showDialog = false}
                          >
                            <span>{this.i18n?.beantragung?.downloadBtnLabel}</span>
                          </c-button>
                          <br/>

                          <c-button
                            centered-content
                            block
                            large
                            onClick={() => {
                              this.showDialog = false;
                            }}
                          >
                            <span>{this.i18n?.beantragung?.cancelBtnLabel}</span>
                          </c-button>

                        </div>
                      </c-container>

                    </c-modal>
                  </div>
                )}

              {!this.offline && this.successMessage && !this.downloadUrl && (
                <div class="error-body">
                  {window.scroll(0, 0)}
                  <c-panel>
                    <c-header type="h2" role="heading">
                      {this.i18n?.error?.errorHeadline }
                        </c-header>
                        <p>
                          { this.i18n?.error?.errorInfo }
                        </p>
                        <c-text>
                          { this.i18n?.error?.errorText }
                        </c-text>
                        <c-dl>
                          <dt>{ this.i18n?.error?.errorTraceIdLabel }</dt>
                          <dd>{this.successMessage?.traceId || "-"}</dd>
                        </c-dl>
                      </c-panel>
                    </div>
                  )}

                  {this.offline && (
                    <div class="offline-steps">
                      {window.scroll(0, 0)}
                      <c-header type="h2">{ this.i18n?.beantragung?.applicationFilledHeadline }</c-header>
                      <c-markdown-renderer>
                        <div slot="source">
                            { this.i18n?.beantragung?.applicationFilledText }
                        </div>                            
                      </c-markdown-renderer>
                      <c-header type="h2">{ this.i18n?.pdf?.whatnextHeadline }</c-header>
                      <ol>
                        <li>
                            <h5>{ this.i18n?.pdf?.whatnextIHeadline }</h5>
                            <c-markdown-renderer>
                                <div slot="source">
                                    { this.i18n?.pdf?.whatnextIText }
                                </div>    
                            </c-markdown-renderer>
                          <button class="download-button" onClick={() => this.download()}>{this.i18n?.beantragung?.createAndDownloadOfflineBtn}</button>
                        </li>
                        <li>
                        <h5>{ this.i18n?.pdf?.whatnextIIHeadline }</h5>
                            <c-markdown-renderer>
                                <div slot="source">
                                  { this.i18n?.pdf?.whatnextIIText }
                                </div>    
                            </c-markdown-renderer>
                        </li>
                        <li>
                            <h5>{ this.i18n?.pdf?.whatnextIIIHeadline }</h5>
                            <c-markdown-renderer>
                                <div slot="source">
                                  { this.i18n?.pdf?.whatnextIIIText }
                                </div>
                            </c-markdown-renderer>
                        </li>
                        <li>
                            <h5>{ this.i18n?.pdf?.whatnextIVHeadline }</h5>
                            <c-markdown-renderer>
                                <div slot="source">
                                  { this.i18n?.pdf?.whatnextIVText }
                                </div>    
                            </c-markdown-renderer>
                        </li>                       
                        
                      </ol>
                    </div>
                  )}

                  <c-modal
                    visible={this.error?.error?.length > 0}
                    flash
                    containerstyle="error"
                    onClose={() => (this.error = null)}
                  >
                  <lb-errorview
                     slot="content"
                     i18n={this.i18n}
                     error={this.error}
                  />
                  </c-modal>
            </Host>
        );
    }
}
